import { useCallback, useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { Me } from '../api/types'

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed-in'; me: Me }
  | { status: 'signed-out' }
  | { status: 'unreachable'; detail: string }

/**
 * Who the browser is signed in as.
 *
 * A 401 is not an error worth logging or showing — it means "show the
 * marketing site". Anything else is a real failure and says so, because a user
 * staring at a login page during an outage deserves to know it is the server
 * and not their password.
 */
export function useSession() {
  const [state, setState] = useState<SessionState>({ status: 'loading' })

  const refresh = useCallback(async () => {
    try {
      setState({ status: 'signed-in', me: await api.session() })
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setState({ status: 'signed-out' })
        return
      }
      setState({
        status: 'unreachable',
        detail: error instanceof Error ? error.message : 'unknown error',
      })
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { state, refresh, setMe: (me: Me) => setState({ status: 'signed-in', me }) }
}
